import {EdgeProps, MarkerType} from 'reactflow';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  Flowchart,
  FlowchartInteractiveEdge,
  FlowchartInteractiveNode,
  FlowchartStaticEdge,
  FlowchartStaticNode,
  WithFlowchartActionFunctions,
  FlowchartRenderData,
  FlowchartActionFunction,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import * as Colors from 'ui-library/Colors';

import {FlowchartValidationErrorType} from '../validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartNodeDetailsPerType} from './ProcessFlowchartNodeDetailType';

export const CHARACTER_THRESHOLD_FOR_WIDE_FLOWCHART_NODE = 25;

export const MAX_FLOWCHART_ZOOM = 1.25;

export const FIT_VIEW_PADDING = 0.2;

export const MIN_FLOWCHART_ZOOM = 0.001;

export const DEFAULT_FIT_VIEW_OPTIONS = {
  padding: FIT_VIEW_PADDING,
};

export const PROCESS_FLOWCHART_EDITOR_PADDING = 36;

export const PROCESS_FLOWCHART_EDGE_DEFAULT_COLOR = Colors.GRAY_DARKER;

export const PROCESS_FLOWCHART_EDGE_ERROR_COLOR = Colors.RED;

// For node types whose names are shown above the node itself, this represents the line height
// taken up by the name.
export const NODE_NAME_HEIGHT = 20;

export function processMarkerEndOptions(hasErrors: boolean = false): {
  type: MarkerType;
  color: string;
  width: number;
  height: number;
} {
  return {
    type: MarkerType.Arrow,
    color: hasErrors ? PROCESS_FLOWCHART_EDGE_ERROR_COLOR : PROCESS_FLOWCHART_EDGE_DEFAULT_COLOR,
    width: 20,
    height: 20,
  };
}

export const PROCESS_FLOWCHART_EDGE_LABEL_SIZE = 12;

export const PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT = 'normal';

export const PROCESS_FLOWCHART_EDGE_LABEL_OPTIONS: Pick<
  EdgeProps,
  'labelStyle' | 'labelBgStyle' | 'labelShowBg' | 'labelBgPadding' | 'labelBgBorderRadius'
> = {
  labelShowBg: true,
  labelStyle: {
    color: Colors.GRAY_DARKER,
    fill: Colors.GRAY_DARKER,
    fontSize: PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
    fontWeight: PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
  },
  labelBgStyle: {background: Colors.GRAY_LIGHTER, fill: Colors.GRAY_LIGHTER},
  labelBgPadding: [8, 8],
};

export enum ProcessFlowchartNodeWidth {
  SIDEBAR = 164,
  DEFAULT = 250,
  WIDE = 400,
  SQUARE = 60,
  DIAMOND = 65,
  PILL = 76,
  CIRCLE = 24,
}

export enum ProcessFlowchartNodeHeight {
  DEFAULT = 54,
  DIAMOND = 65,
  PILL = 36,
  CIRCLE = 24,
}

export const interactiveNodeWrapperBorderWidth = 10;
export const sourceHandleWrapperPadding = 16;

export enum ProcessFlowchartNodeEdgeLength {
  DIAMOND = 46,
}

export const AUTOLAYOUT_NODE_PADDING = 4; // this will translate to 2px on each side of the node

export const ROUTER_AND_END_NODE_NAME_MAX_LENGTH = 25;

/**
 * Constants for rendering multiple target handles
 */
export const TARGET_HANDLE_AREA_WIDTH = 39;

export const TARGET_HANDLE_AREA_START_OFFSET_DEFAULT_NODE = 105;

export const TARGET_HANDLE_AREA_START_OFFSET_NARROW_NODE = 38;

export enum ProcessFlowchartEdgeType {
  INTERACTIVE_BUILDER = 'INTERACTIVE_BUILDER',
  STATIC_VIEWER = 'STATIC_VIEWER',
}

export enum ProcessFlowchartNodeType {
  START_NODE = 'START_NODE',
  MIDDLE_NODE = 'MIDDLE_NODE',
  END_NODE = 'END_NODE',
  DISCONNECTED_NODE = 'DISCONNECTED_NODE',
}

export const ProcessFlowchartHandleClassName = {
  // These classes are added to flowchart handles for querying purposes only (not styling)
  SOURCE: '_source_flowchart_handle',
  TARGET: '_target_flowchart_handle',
};

export enum ProcessFlowchartUIOnlyNodeType {
  COLLAPSED_NODE = 'COLLAPSED_NODE',
}

export type ProcessFlowchartNodeDataType = ProcessFlowchartNodeDetailsType | ProcessFlowchartUIOnlyNodeType;

/**
 * Interfaces for rendering a static flowchart - a static flowchart is rendered
 * in the workspace tab as purely presentational and does not have interactive edges or nodes.
 */
export interface ProcessFlowchartStaticNodeAnalyticsData {
  rootProcessId?: string | null;
}
export interface ProcessFlowchartStaticNodeStatusCount {
  statusType: ProcessTaskStatusType;
  count: number;
  label?: string;
}

export interface ProcessFlowchartStaticNode extends FlowchartStaticNode {
  type: ProcessFlowchartNodeDataType;
  linkUrl?: string | null;
  statusCounts?: Array<ProcessFlowchartStaticNodeStatusCount>;
  analyticsData?: ProcessFlowchartStaticNodeAnalyticsData;
  outboundEdgeIdToLabel?: Record<string, string>;
  numNodesRepresented?: number;
  errors?: Array<string> | null;
}

export interface ProcessFlowchartStaticEdge extends FlowchartStaticEdge {
  hasBeenUsedToCreateData?: boolean;
}

export type ProcessStaticFlowchart = Flowchart<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge>;

/**
 * Interfaces for rendering an interactive flowchart - an interactive GraphBuilder flowchart has
 * editable nodes and edges.
 */

export enum ProcessFlowchartEdgeConnectionType {
  // Multiple handles are used for rendering parallel edges
  MULTIPLE = 'MULTIPLE',
  // A single handle is used to render incoming LOYSP edges, or just a regular edge
  SINGLE = 'SINGLE',
}

export interface ProcessFlowchartEdgeConnection {
  type: ProcessFlowchartEdgeConnectionType;
  incomingEdges: Array<string>;
}

export type ProcessFlowchartInteractiveNode = FlowchartInteractiveNode &
  ProcessFlowchartNodeDetailsPerType & {
    type: ProcessFlowchartNodeDetailsType;
    linkUrl?: string | null;
    statusCounts?: Array<ProcessFlowchartStaticNodeStatusCount>;
    incomingEdgeConnection?: ProcessFlowchartEdgeConnection;
    isOutputNode?: boolean;
    errors?: Array<string> | null;
    errorTypes?: Array<FlowchartValidationErrorType>;
  };

export interface ProcessFlowchartInteractiveEdge extends FlowchartInteractiveEdge {
  hasBeenUsedToCreateData?: boolean;
  errors?: Array<string> | null;
  errorTypes?: Array<FlowchartValidationErrorType>;
}

export interface ProcessTaskSchemaForInteractiveFlowchart {
  id: string;
  name: string;
}

export type ProcessInteractiveFlowchart = Flowchart<
  ProcessFlowchartInteractiveNode,
  ProcessFlowchartInteractiveEdge
>;

export type ActionFunctionsForInteractiveNode = Array<
  FlowchartActionFunction<ProcessFlowchartInteractiveNode, ProcessInteractiveFlowchart>
>;

export type ActionFunctionsPerInteractiveNodeType = Record<
  ProcessFlowchartNodeDetailsType,
  ActionFunctionsForInteractiveNode
>;

export const ROOT_NODE_CLIENT_ID = 'root';
export const OUTPUT_NODE_CLIENT_ID = 'output';

export type ProcessFlowchartInteractiveNodeWithActionFunctions =
  WithFlowchartActionFunctions<ProcessFlowchartInteractiveNode>;
export type ProcessFlowchartInteractiveEdgeWithActionFunctions =
  WithFlowchartActionFunctions<ProcessFlowchartInteractiveEdge>;

export type ProcessFlowchartRenderData = FlowchartRenderData<
  ProcessFlowchartInteractiveNodeWithActionFunctions,
  ProcessFlowchartInteractiveEdgeWithActionFunctions
>;
